'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { Listbox, Transition } from '@headlessui/react';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { twJoin } from 'tailwind-merge';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { OpenerCheckResultsSubmissionType } from '@/ui/components/tiles/OpenerCompatibilityCheck/types/OpenerCheckResultsSubmission';
import Cookies from 'js-cookie';
import { OPENER_CHECK_COOKIE_NAME } from '@/utils/constants';
import { v4 as uuid } from 'uuid';
import { OpenerCompatibilityCheckTile } from './types/OpenerCompatibilityCheck';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { IntercomBrand } from './types/IntercomBrand';
import { IntercomModel } from './types/IntercomModel';
import Button from '../../shared/Button';
import InfoDialog from './components/InfoDialog';
import HelpScreen from './components/HelpScreen';
import NotFoundScreen from './components/NotFoundScreen';
import IsCompatibleScreen from './components/IsCompatibleScreen';
import NonCompatibleScreen from './components/NonCompatibleScreen';

export const getOpenerCheckIDFromCookie = ():
  | { openerCheckId: string; newOpenerCheck: boolean }
  | undefined => {
  try {
    const existingOpenerCheck = Cookies.get(OPENER_CHECK_COOKIE_NAME);
    if (existingOpenerCheck !== undefined && existingOpenerCheck !== null) {
      return { openerCheckId: existingOpenerCheck, newOpenerCheck: false };
    }
    const newOpenerCheckID = `Opener-${uuid()}`;
    Cookies.set(OPENER_CHECK_COOKIE_NAME, newOpenerCheckID, { expires: 365 });
    return { openerCheckId: newOpenerCheckID, newOpenerCheck: true };
  } catch (e) {
    console.error('Error getting Opener Check ID from cookie:', e);
    return undefined;
  }
};

export default function OpenerCompatibilityCheck({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: OpenerCompatibilityCheckTile;
  trackingPrefix?: string;
}>) {
  const [allBrands, setAllBrands] = useState<IntercomBrand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<IntercomBrand>({
    brandId: 0,
    brand: 'Choose manufacturer',
  });
  const [allModels, setAllModels] = useState<IntercomModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<IntercomModel>({
    verified: 0,
    model: 'Choose model',
    intercomId: 0,
  });
  const [dialogContent, setDialogContent] = useState<React.ReactNode | null>(null);
  const initialScreenChunk = tile['initial-screen'].chunks?.[0];
  const title = extractComponentFromChunk(initialScreenChunk, 'title', 'SingleLineContent')?.text;
  const description = extractComponentFromChunk(
    initialScreenChunk,
    'description',
    'RichTextContent',
  );
  const image = extractComponentFromChunk(initialScreenChunk, 'image', 'ImageContent');
  const { language } = useLocalization();

  useEffect(() => {
    async function fetchBrands() {
      try {
        const { data }: { data: IntercomBrand[] } = await axios.get(
          'https://api.nuki.io/opener/brand',
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
            },
          },
        );

        // sort brands alphabetically by the brand name
        const sortedBrands = data.sort((a, b) => a.brand.localeCompare(b.brand));

        setAllBrands(sortedBrands);
      } catch (error) {
        console.error('Error fetching data from Nuki API:', error);
      }
    }
    fetchBrands();
  }, []);

  // When a brand is selected fetch all models for that brand
  useEffect(() => {
    async function fetchModels() {
      try {
        const { data }: { data: IntercomModel[] } = await axios.get(
          `https://api.nuki.io/opener/intercom?brandId=${selectedBrand.brandId}`,
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
            },
          },
        );
        const sortedModels = data.sort((a, b) => a.model.localeCompare(b.model));
        setAllModels(sortedModels);
      } catch (error) {
        console.error('Error fetching data from Nuki API:', error);
      }
    }
    if (selectedBrand.brandId !== 0) {
      setSelectedModel({ verified: 0, model: 'Choose model', intercomId: 0 });
      fetchModels();
    }
  }, [selectedBrand]);

  const syncOpenerCheckResultsToDb = async (
    openerCheckResults: OpenerCheckResultsSubmissionType,
  ): Promise<void> => {
    try {
      const response = await axios.post(apiPaths.opener.storeCheckResults, openerCheckResults);
    } catch (e) {
      console.error('Error saving opener check answer:', e);
    }
  };

  const onSubmit = () => {
    const intercomIsNukiCompatible = selectedModel.verified === 1;
    const { brand, brandId } = selectedBrand;
    const { model: intercom, intercomId } = selectedModel;

    const toBeSubmitted = {
      brandName: brand,
      brandId,
      intercomModel: intercom,
      intercomId,
      language,
      intercomIsNukiCompatible,
    };

    // send opener check results to the backend, subsequently to the database
    const openerCheckMetaInfo = getOpenerCheckIDFromCookie();
    openerCheckMetaInfo !== undefined &&
      syncOpenerCheckResultsToDb({
        openerCheckId: openerCheckMetaInfo.openerCheckId,
        newOpenerCheck: openerCheckMetaInfo.newOpenerCheck,
        checkData: toBeSubmitted,
      });

    if (intercomIsNukiCompatible) {
      setDialogContent(
        <IsCompatibleScreen chunk={tile['is-compatible-screen']} trackingPrefix={trackingPrefix} />,
      );
    } else {
      setDialogContent(<NonCompatibleScreen chunk={tile['non-compatible-screen']} />);
    }
  };

  return (
    <Container className="overflow-visible">
      <InfoDialog dialogContent={dialogContent} setDialogContent={setDialogContent} />
      <div className="grid gap-8 md:grid-cols-2 md:gap-14">
        <OptimizedImage
          imageComponent={image}
          sizes="(max-width: 768px) 100vw, 30vw"
          width={500}
          height={500}
          className="mx-auto aspect-video rounded-lg object-cover md:aspect-square"
        />
        <div className="my-auto max-w-md space-y-4">
          <div>
            <h2 className="mb-4 w-fit text-2xl font-bold sm:mb-6 sm:text-3xl">
              {/* render each space and word as a span with class yellow underline to render
               the yellow underline correctly */}
              {title?.split(' ').map((word, index) => (
                <span key={index} className="yellow-underline">
                  {word}
                  {index !== title.split(' ').length - 1 && ' '}
                </span>
              ))}
            </h2>
            <RichTextRenderer blocks={description} className="sm:text-md" />
          </div>
          <div className="space-y-4">
            <Listbox value={selectedBrand} onChange={setSelectedBrand}>
              {({ open }) => (
                <div className="relative">
                  <Listbox.Button
                    className={twJoin(
                      'flex w-full items-center justify-between gap-3 rounded-md border border-neutral-40 p-2 text-neutral-50 duration-200 ease-in hover:bg-neutral-5',
                      open && 'bg-neutral-5',
                    )}
                  >
                    <span>{selectedBrand?.brand}</span>
                    <ChevronDownIcon className="h-5 w-auto" aria-hidden="true" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    enterFrom="opacity-0"
                    enter="transition ease-out duration-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute left-0 right-0 top-full z-10 max-h-xs overflow-y-auto rounded-md border border-neutral-40 bg-white">
                      {allBrands.map((brand) => (
                        <Listbox.Option
                          key={brand.brandId}
                          className={({ selected }) =>
                            twJoin(
                              'relative cursor-pointer select-none py-2 pl-10 pr-4 duration-100 ease-in hover:bg-neutral-5',
                              selected && 'bg-neutral-5',
                            )
                          }
                          value={brand}
                        >
                          {({ selected }) => (
                            <>
                              <span className={twJoin('block', selected && 'font-bold')}>
                                {brand.brand}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>
            {allModels.length > 0 && (
              <div>
                <Listbox value={selectedModel} onChange={setSelectedModel}>
                  {({ open }) => (
                    <div className="relative">
                      <Listbox.Button
                        className={twJoin(
                          'flex w-full items-center justify-between gap-3 rounded-md border border-neutral-40 p-2 text-neutral-50 duration-200 ease-in hover:bg-neutral-5',
                          open && 'bg-neutral-5',
                        )}
                      >
                        <span>{selectedModel?.model}</span>
                        <ChevronDownIcon className="h-5 w-auto" aria-hidden="true" />
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        enterFrom="opacity-0"
                        enter="transition ease-out duration-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute left-0 right-0 top-full z-10 max-h-xs overflow-y-auto rounded-md border border-neutral-40 bg-white">
                          {allModels.map((model) => (
                            <Listbox.Option
                              key={model.intercomId}
                              className={({ selected }) =>
                                twJoin(
                                  'relative cursor-pointer select-none py-2 pl-10 pr-4 duration-100 ease-in hover:bg-neutral-5',
                                  selected && 'bg-neutral-5',
                                )
                              }
                              value={model}
                            >
                              {({ selected }) => (
                                <>
                                  <span className={twJoin('block', selected && 'font-bold')}>
                                    {model.model}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  )}
                </Listbox>
                <button
                  onClick={() => setDialogContent(<HelpScreen chunk={tile['help-screen']} />)}
                  className="mt-2 text-sm underline "
                >
                  Help
                </button>
              </div>
            )}
          </div>
          <div className="mx-auto space-y-3 sm:mx-0">
            {selectedModel.intercomId !== 0 && (
              <Button onClick={onSubmit} className="mx-auto w-fit sm:mx-0">
                Submit
              </Button>
            )}
            <button
              onClick={() =>
                setDialogContent(
                  <NotFoundScreen
                    afterSubmit={() => setDialogContent(null)}
                    chunk={tile['not-found-screen']}
                  />,
                )
              }
              className="mx-auto block w-fit font-medium underline sm:mx-0"
            >
              Manufacturer or model not found?
            </button>
          </div>
        </div>
      </div>
    </Container>
  );
}
